import { EApiMethods } from '@/types/enums/api'
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { camelizeKeys, decamelizeKeys } from 'humps'
import localforage from 'localforage'
import jwtdecode, { JwtPayload } from 'jwt-decode'

const api = axios
const namespace = `${process.env.PACKAGE_NAME || 'app'}`

const readTokenFromStorage = async () => {
    const storageToken = (await localforage.getItem(`${namespace}.token`)) as string
    // validate storageToken
    if (storageToken) {
        const decodedToken: JwtPayload = jwtdecode(storageToken)
        if (decodedToken.exp && decodedToken.exp * 1000 > Date.now()) {
            return storageToken
        }
    }
    return ''
}

api.defaults.baseURL = process.env.VUE_APP_API
api.defaults.headers.common.Accept = 'application/json'
api.defaults.headers.common['Content-Type'] = 'application/json'
api.interceptors.response.use((response: AxiosResponse) => {
    if (response.data && response.headers['content-type'] === 'application/json') {
        response.data = camelizeKeys(response.data)
    }
    return response
})
api.interceptors.request.use(
    async config => {
        let token = ''
        try {
            token = await readTokenFromStorage()
        } catch (error) {
            Promise.reject(error)
        } finally {
            config.headers.common.Authorization = `Bearer ${token}`
        }
        const newConfig = { ...config }
        if (config.data) {
            newConfig.data = decamelizeKeys(config.data)
        }
        return newConfig
    },
    error => {
        return Promise.reject(error)
    },
)

export const useApi = (): {
    useGet: (url: string, params?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse<any>>
    usePost: (url: string, params?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse<any>>
    usePatch: (
        url: string,
        params?: any,
        config?: AxiosRequestConfig,
    ) => Promise<AxiosResponse<any>>
    useDelete: (url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<any>>
    readTokenFromStorage: () => Promise<string>
} => {
    const fetch = async (
        method: EApiMethods,
        url: string,
        params?: any,
        config?: AxiosRequestConfig,
    ) => {
        try {
            const _params_ = decamelizeKeys(params)
            switch (method) {
                case EApiMethods.POST: {
                    return await api.post(url, _params_, config)
                }
                case EApiMethods.PATCH: {
                    return await api.patch(url, _params_, config)
                }
                case EApiMethods.GET: {
                    return await api.get(url, {
                        ..._params_,
                        ...config,
                    })
                }
                case EApiMethods.DELETE: {
                    return await api.delete(url, config)
                }
            }
        } catch (error) {
            return Promise.reject(error)
        }
    }

    const useGet = async (url: string, params?: any, config?: AxiosRequestConfig) => {
        try {
            return fetch(EApiMethods.GET, url, params, config)
        } catch (error) {
            return Promise.reject(error)
        }
    }

    const usePost = async (url: string, params?: any, config?: AxiosRequestConfig) => {
        return fetch(EApiMethods.POST, url, params, config)
            .then(res => res)
            .catch(error => {
                if (axios.isAxiosError(error)) {
                    const messageFromMessage = error.response?.data.message
                    const messageFromData = error.response?.data.data?.error

                    if (messageFromMessage) error.message = messageFromMessage
                    else if (messageFromData) error.message = messageFromData
                }
                return Promise.reject(error)
            })
    }
    const usePatch = async (url: string, params?: any, config?: AxiosRequestConfig) => {
        return fetch(EApiMethods.PATCH, url, params, config)
            .then(res => res)
            .catch(error => {
                let message
                if (axios.isAxiosError(error)) {
                    message = error.response?.data.data.error
                    error.message = message
                }
                return Promise.reject(error)
            })
    }
    const useDelete = async (url: string, config?: AxiosRequestConfig) => {
        return fetch(EApiMethods.DELETE, url, config)
            .then(res => res)
            .catch(error => {
                let message
                if (axios.isAxiosError(error)) {
                    message = error.response?.data.data.error
                    error.message = message
                }
                return Promise.reject(error)
            })
    }
    return {
        useGet,
        usePost,
        usePatch,
        useDelete,
        readTokenFromStorage,
    }
}
